import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useForm} from 'react-hook-form';
import {translations} from '../../../i18n/translations';
import {Alert, Grid, Skeleton, SkeletonProps, Stack} from '@mui/material';
import {Modal} from '../../modal/modal';
import {DynamicForm, DynamicFormFieldShape} from '../../dynamic-form';
import {LoadingConfigurationsType} from '../../../enums';
import {useAuth} from '../../../contexts/auth/context';
import {CarePlanConfiguration} from '../../../backend/interfaces/care-plan-configuration';
import {convertArrayToIndex} from '../../../utils/functions/convert-array-to-index';
import {EntityActivitiesRepository} from '../../../repositories/entity-activities';
import {DEFAULT_FORM_VALIDATIONS} from '../../../utils/constants';
import {ConfigurationsIndex} from '../../../types';
import {ActivityType} from '../../../backend/interfaces/activity-type';
import {CarePlanActivitiesGroupApiRequestConfigurationsItem} from '../../../backend/requests/care-plan-activities-group';
import {EntityUserCarePlansRepository} from '../../../repositories/entity-user-care-plans';
import {useSnackBar} from '../../../contexts/snack-bar/context';
import {CarePlanActivitiesGroup} from '../../../backend/interfaces/care-plan-activities-group';
import {CarePlanConfigurationLayout} from '../../../backend/interfaces/care-plan-configuration-layout';
import {CarePlanActivitiesGroupType} from '../../../backend/interfaces/care-plan-activities-group-type';
import {EntityTemplatesRepository} from '../../../repositories/entity-templates';
import {useParams} from 'react-router-dom';

interface AddActivityEntryDialogProps {
  open: boolean;
  entry: CarePlanActivitiesGroup;
  activityGroupTypeId: string;
  seniorUserId?: string;
  activitiesSectorId: string;
  repetitionType?: 'activities-group' | 'activity-type';
  onClose: () => void;
  onUpdate: (entry: CarePlanActivitiesGroup) => void;
}

const SKELETON_DEFAULT_PROPS = {
  animation: 'wave',
  variant: 'rounded',
} as SkeletonProps;

export const AddActivityEntryDialog = ({
  open,
  onClose,
  entry,
  seniorUserId,
  activitiesSectorId,
  activityGroupTypeId,
  onUpdate,
  repetitionType,
}: AddActivityEntryDialogProps) => {
  const {t} = useTranslation();
  let {entityId, templateId}: {entityId: string; templateId: string} = useParams();
  const [activityType, setActivityType] = useState<ActivityType | undefined>(undefined);
  const [layout, setLayout] = useState<CarePlanConfigurationLayout | undefined>(undefined);
  const [loadingConfigurations, setLoadingConfigurations] = useState<LoadingConfigurationsType>(
    LoadingConfigurationsType.Loading
  );
  const [configurationsIndex, setConfigurationsIndex] = useState<ConfigurationsIndex>(undefined);
  const formInstance = useForm<DynamicFormFieldShape>({
    ...DEFAULT_FORM_VALIDATIONS,
  });
  const authContext = useAuth();
  const snackBarContext = useSnackBar();

  const handleModalClose = () => {
    // reset form with empty values to clear all fields
    formInstance.reset({});

    // reset state variables to clear cached values
    setActivityType(undefined);
    setLayout(undefined);
    setConfigurationsIndex(undefined);
    setLoadingConfigurations(LoadingConfigurationsType.Loading);

    // close modal after cleanup
    onClose();
  };

  const loadActivityGroupEntryTypeConfigurations = useCallback(() => {
    // Immediately reset form to prevent showing previous activity's data
    formInstance.reset({});

    // Start Loading Configurations
    setLoadingConfigurations(LoadingConfigurationsType.Loading);

    // Obtain data
    Promise.all<[Promise<ActivityType | null>, Promise<Array<CarePlanConfiguration>>]>([
      // Load Activity Type (to obtain the Configuration layout)
      EntityActivitiesRepository.instance.getType(
        authContext.state.session?.selectedAffiliation?.entity.id ?? '',
        activitiesSectorId,
        entry.activitiesGroupId,
        activityGroupTypeId
      ),

      // Load Activity Type Configurations (to list the configuration values)
      EntityActivitiesRepository.instance.getTypeCarePlanConfigurations(
        authContext.state.session?.selectedAffiliation?.entity.id ?? '',
        activitiesSectorId,
        entry.activitiesGroupId,
        activityGroupTypeId
      ),
    ])
      .then(
        ([activityType, careplanConfigurations]: [
          ActivityType | null,
          Array<CarePlanConfiguration>,
        ]) => {
          if (activityType) {
            // set activity
            setActivityType(activityType);

            // index activity configurations
            setConfigurationsIndex(
              convertArrayToIndex<CarePlanConfiguration>(careplanConfigurations, 'id')
            );

            // set modal layout
            setLayout(activityType.carePlanConfigurationLayout);

            // Stop Loading
            setLoadingConfigurations(LoadingConfigurationsType.Loaded);
          } else {
            // Not found
            setLoadingConfigurations(LoadingConfigurationsType.Error);
          }
        }
      )
      .catch((error: any) => {
        // Log
        console.log(error);

        // Stop Loading
        setLoadingConfigurations(LoadingConfigurationsType.Error);
      });
  }, [
    activitiesSectorId,
    authContext.state.session?.selectedAffiliation?.entity.id,
    entry.activitiesGroupId,
    activityGroupTypeId,
    formInstance,
  ]);

  const handleAddActivityTypeEntry = (
    formData: CarePlanActivitiesGroupApiRequestConfigurationsItem[]
  ) => {
    if (!activityGroupTypeId) throw new Error('Empty activity group entry id');

    if (seniorUserId) {
      // add user care plan activity type
      EntityUserCarePlansRepository.instance
        .addActivityTypeToCarePlan(
          authContext.state.session?.selectedAffiliation?.entity.id ?? '',
          seniorUserId,
          activitiesSectorId,
          entry.id ?? '',
          {
            activityTypeId: activityGroupTypeId,
            configurations: formData,
          }
        )
        .then((response) => {
          snackBarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(translations.activities_type_entry.snackbar.update.success),
          });

          // update callback
          let updatedEntry = {...entry};

          if (!updatedEntry.activityTypes) updatedEntry.activityTypes = [];

          if (updatedEntry.activityTypes && response) {
            updatedEntry.activityTypes = [
              ...updatedEntry.activityTypes,
              response,
            ] as CarePlanActivitiesGroupType[];

            // Fire update
            onUpdate(updatedEntry);
          }
        })
        .catch((err) => {
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.activities_type_entry.snackbar.update.error),
          });
          console.error(err);
        });
    } else {
      // add template activity type
      EntityTemplatesRepository.instance
        .addTemplateActivityGroupType(entityId, templateId, entry.id, {
          activityTypeId: activityGroupTypeId,
          configurations: formData,
        })
        .then((response) => {
          snackBarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(translations.activities_type_entry.snackbar.update.success),
          });

          // update callback
          let updatedEntry = {...entry};

          if (!updatedEntry.activityTypes) updatedEntry.activityTypes = [];

          if (updatedEntry.activityTypes && response) {
            updatedEntry.activityTypes = [
              ...updatedEntry.activityTypes,
              response,
            ] as CarePlanActivitiesGroupType[];

            // Fire update
            onUpdate(updatedEntry);
          }
        })
        .catch((err) => {
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.activities_type_entry.snackbar.update.error),
          });
          console.error(err);
        });
    }

    handleModalClose();
  };

  useEffect(() => {
    if (open) loadActivityGroupEntryTypeConfigurations();
  }, [loadActivityGroupEntryTypeConfigurations, open]);

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      title={t(translations.activities_type_entry.modals.edit.title, {type: activityType?.name})}
      aria-labelledby="add-activity-group-entry-dialog-title"
      aria-describedby="add-activity-group-entry-dialog-description"
      maxWidth="sm"
      PaperProps={{sx: {maxWidth: '576px'}}}
      fullWidth
      preventCloseClick
      actions={[
        {
          label: t(translations.modal.cancel),
          type: 'destructive',
          onClick: handleModalClose,
        },
        {
          label: t(translations.modal.save),
          type: 'nondestructive',
          disabled: !formInstance.formState.isValid,
          form: 'add-activity-type-entry',
          buttonType: 'submit',
        },
      ]}
    >
      {loadingConfigurations === LoadingConfigurationsType.Loading && (
        <Grid container spacing={4}>
          {new Array(6).fill(1).map((_item: number, index: number) => (
            <Grid item xs={6} key={`skeleton_edit_sub_activity_${index}`}>
              <Stack gap={2} display="flex" flexDirection="column">
                <Skeleton {...SKELETON_DEFAULT_PROPS} width={120} height={20} />
                <Skeleton {...SKELETON_DEFAULT_PROPS} width="100%" height={42} />
              </Stack>
            </Grid>
          ))}
        </Grid>
      )}

      {loadingConfigurations === LoadingConfigurationsType.Error && (
        <Alert severity="error">{t(translations.dynamic_form.error_loading)}</Alert>
      )}

      {loadingConfigurations === LoadingConfigurationsType.Loaded && (
        <DynamicForm
          key={`add-activity-type-entry-${activityGroupTypeId}-${open}`}
          id="add-activity-type-entry"
          activityGroupId={entry.activitiesGroupId}
          activityTypeId={activityGroupTypeId}
          activitiesSectorId={activitiesSectorId}
          formInstance={formInstance}
          configurationsIndex={configurationsIndex}
          layout={layout}
          onSubmit={handleAddActivityTypeEntry}
          configuredRepetition={repetitionType}
        />
      )}
    </Modal>
  );
};
